import React, { useState } from "react";

import { useQuery } from "@tanstack/react-query";
import classnames from "classnames";
import { useTranslation } from "react-i18next";

import { ExternalList } from "../../../context/MovingResources";
import { useAppState } from "../../../context/StateContext";
import { Destination } from "../../../models/destination";
import { Item } from "../../../models/item";
import { JobMode } from "../../../models/job.mode";
import { Location, DifferentLocation as LocationAddress } from "../../../models/location";
import { RoomJob } from "../../../models/sales";
import { ServiceBooking } from "../../../models/service.booking";
import DestinationService from "../../../services/destination.service";
import ModeJobService from "../../../services/mode.job.service";
import { Status } from "../../../util/enums";
import { getBrandCustom } from "../../../util/getCustomColor";
import { getDestinations, getValueFromEachDestination } from "../../../util/getDestinations";
import BoxForm from "../../Form/Box";
import InfoSection from "./InfoSection";
import RoomInfoListSection from "./RoomInfoListSection";
import useStyles, { EmptyInventoryImg, EmptyInventoryText, EmptyInventoryWrapper } from "./styles";
import TabsListSection from "./TabsListSection";
import { useAction } from "./useActions";

const EMPTY_INVENTORY_IMG = require("../../../assets/img/empty-documents.png");

interface QuotedInventoryProps {
  inventory: RoomJob[];
  jobId: number;
  defaultMode: string;
  locations: LocationAddress[];
  viewOnly?: boolean;
  update?: (value: RoomJob[]) => void;
  externalList?: ExternalList;
  isOnViewMode?: boolean;
  onCustomerView?: boolean;
  noHideOnExpand?: boolean;
}

const QuotedInventory = ({
  inventory,
  locations,
  jobId,
  defaultMode,
  viewOnly = false,
  update,
  externalList,
  isOnViewMode = false,
  onCustomerView = false,
  noHideOnExpand,
}: QuotedInventoryProps) => {
  const { currentUser } = useAppState();

  const classes = useStyles(isOnViewMode)();

  const [roomNameSelected, setRoomNameSelected] = useState<string>("");
  const [destinationSelected, setDestinationSelected] = useState<Destination>();

  const [customDestinations, setCustomDestinations] = useState<Destination[]>(
    externalList?.destinationList || []
  );
  const [modes, setModes] = useState<JobMode[]>(externalList?.jobModeList || []);

  const { t: translate } = useTranslation();

  useQuery(
    ["destination.list"],
    () =>
      DestinationService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["name"]]),
      }),
    {
      enabled: !!currentUser?.companyId && !externalList?.destinationList,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setCustomDestinations(response.data);
      },
    }
  );

  useQuery(
    ["mode.list"],
    () =>
      ModeJobService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["name"]]),
      }),
    {
      enabled: !!currentUser?.companyId && !externalList?.jobModeList,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data instanceof Array) setModes(response.data);
      },
    }
  );

  const { handleAddRoom, handleAddItemToRoom, handleAddMaterialToRoom } = useAction({
    data: inventory,
    defaultMode,
    jobId,
    update: (value) => update && update(value),
  });

  const roomData = () => {
    if (roomNameSelected !== "" && !noHideOnExpand) {
      const roomSelected = inventory.find((room: RoomJob) => room.name === roomNameSelected);

      if (roomSelected) return [roomSelected];
    }

    return inventory;
  };

  const hasInfoSection = (): boolean => {
    if (inventory?.length > 0) {
      const hasItemJob =
        inventory?.map((room) => room.itemsJob?.length > 0).filter((item) => item === true)
          ?.length > 0;
      const hasMaterial =
        inventory
          ?.map((room) => {
            if (room.materials.length > 0) {
              const isMaterialAnItem =
                room.materials
                  .map((material) => material?.serviceBooking?.isItem)
                  .filter((material) => material === true)?.length > 0;

              return isMaterialAnItem;
            }

            return false;
          })
          .filter((material) => material === true)?.length > 0;

      return hasItemJob || hasMaterial;
    }

    return false;
  };

  const renderInfoSection = () => {
    if (hasInfoSection())
      return (
        <BoxForm
          customClasses={classes.box}
          background={getBrandCustom("headerBackgroundColor", "#EDF5FB")}
        >
          <InfoSection
            inventory={inventory}
            modes={modes}
            destinationList={customDestinations}
            destinationAddresses={locations as Location[]}
          />
        </BoxForm>
      );

    return null;
  };

  const renderTabListSection = () => {
    if (viewOnly) return null;

    return (
      <>
        <div className={classes.separator} />
        <div className={classes.tabs}>
          <TabsListSection
            jobId={jobId}
            externalList={externalList}
            roomNameSelected={roomNameSelected}
            addRoom={(room: RoomJob) => handleAddRoom(room)}
            addItemIntoRoom={(item: Item) =>
              handleAddItemToRoom(
                item,
                roomNameSelected,
                currentUser?.company,
                getValueFromEachDestination(destinationSelected, locations)?.destination,
                getValueFromEachDestination(destinationSelected, locations)?.destinationResidence,
                getValueFromEachDestination(destinationSelected, locations)?.modeDestination
              )
            }
            addMaterialIntoRoom={(service: ServiceBooking) =>
              handleAddMaterialToRoom(
                roomNameSelected,
                service,
                currentUser?.company,
                getValueFromEachDestination(destinationSelected, locations)?.destination,
                getValueFromEachDestination(destinationSelected, locations)?.destinationResidence,
                getValueFromEachDestination(destinationSelected, locations)?.modeDestination
              )
            }
          />
        </div>
      </>
    );
  };

  if (viewOnly && !inventory?.length) {
    return (
      <EmptyInventoryWrapper>
        <EmptyInventoryImg src={EMPTY_INVENTORY_IMG} alt="empty-inventory-img" />
        <EmptyInventoryText>{translate("noItemsInQuotedInventory")}</EmptyInventoryText>
      </EmptyInventoryWrapper>
    );
  }

  return (
    <>
      {renderInfoSection()}
      <div
        className={classnames(
          classes.mainGrid,
          hasInfoSection() ? classes.heightWithInfoSection : classes.heightWithoutInfoSection
        )}
      >
        <div className={classes.info}>
          <RoomInfoListSection
            data={roomData()}
            locations={locations}
            allData={inventory}
            update={(value) => update && update(value)}
            jobId={jobId}
            dataForSelectRoom={inventory}
            destinations={getDestinations(customDestinations, locations, modes)}
            roomIdSelected={roomNameSelected}
            destination={destinationSelected}
            setRoomIdSelected={(value: string) => setRoomNameSelected(value)}
            setDestination={(value: Destination) => setDestinationSelected(value)}
            viewOnly={viewOnly}
            onCustomerView={onCustomerView}
            noHideOnExpand={noHideOnExpand}
          />
        </div>
        {renderTabListSection()}
      </div>
    </>
  );
};

export default QuotedInventory;
